"use client"

import styled from "@emotion/styled"
import { Box } from "@mui/material"
import { useInView } from "react-intersection-observer"
import { NewText, transitionDelayTime, TransitionUp, useInViewConfig } from "ui"
import ImageWithAlt from "ui/ImageWithAlt"
import { getUmbracoMediaUrl } from "utils"
import { RentProjectAdvantages, RentProjectAdvantagesItem } from "utils/projects"
import WhiteBgHeading from "./WhiteBgHeading"

const StyledAdvantages = styled.section`
  --benefits-black: #011727;
  display: flex;
  justify-content: center;
  overflow: hidden;
  background-color: ${(props) => props.theme.colors.advantagesBg4rentMarketing};
  padding-block: 60px;
`

const StyledContainer = styled.div<{ locale: string }>`
  display: flex;
  flex-direction: column;
  gap: 32px;

  max-width: 1240px;
  width: 100%;

  gap: 16px;

  .advantages-image {
    object-fit: contain;
    aspect-ratio: 263/288;
    width: 100%;
    max-width: 526px;
    height: max-content;
    order: -1;
    align-self: center;
    padding: 5px;
  }
  ${(props) => props.theme.breakpoints.up("md")} {
    padding-inline: 20px;
    gap: 0;
    flex-direction: row;
    .advantages-image {
      order: 0;
      height: 100%;
      width: unset;
      flex-shrink: 0;
      align-self: unset;
      padding: 0;
    }
  }
`

const StyledTextContent = styled.div<{ locale: string }>`
  display: flex;
  flex-direction: column;
  width: 100%;
  gap: 32px;

  padding-inline: 20px;
  ${(props) => props.theme.breakpoints.up("md")} {
    padding-inline: 0;
    gap: 40px;
    flex: 1;
    flex-shrink: 0;
    min-width: 57%;
  }
`

const StyledList = styled.ul`
  display: flex;
  flex-direction: column;
  width: 100%;
  gap: 32px;
  padding: 0;

  ${(props) => props.theme.breakpoints.up("md")} {
    gap: 24px;
  }
`

const StyledListItem = styled.li<{ locale: string }>`
  display: flex;
  width: 100%;
  gap: 16px;
`
const StyledListItemP = styled(NewText)`
  margin: 0;
  ${(props) => props.theme.breakpoints.up("md")} {
    letter-spacing: -0.36px;
  }
`

const StyledListItemTextContent = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 4px;
  color: var(--benefits-black);
  align-self: flex-start;
  font-family: var(--narkis);
`

const IconImage = styled.div`
  display: flex;
  background: #fff;
  justify-content: center;
  align-items: center;
  border-radius: 12px;
  padding: 8px;
  flex-shrink: 0;
  align-self: flex-start;
  width: 62px;
  height: 62px;
  img {
    width: 46px;
    height: 46px;
  }
`

type AdvantageListItemProps = {
  item: RentProjectAdvantagesItem
  locale: string
}
const AdvantageListItem = ({ item, locale }: AdvantageListItemProps) => {
  const title = item?.title
  const text = item?.text
  let icon = item?.icon
  if (icon) {
    icon = getUmbracoMediaUrl(icon)
  }
  return (
    <StyledListItem locale={locale}>
      <IconImage>
        <ImageWithAlt
          image={{
            altText: title,
            url: icon,
          }}
          width={46}
          height={46}
        />
      </IconImage>
      <StyledListItemTextContent>
        <NewText
          component="h6"
          desktopFontSize={20}
          mobileFontSize={18}
          desktopFontWeight={700}
          mobileLineHeight={"24px"}
          desktopLineHeight={"24px"}
          color="inherit"
          sx={{
            maring: 0,
          }}
        >
          {title}
        </NewText>
        <StyledListItemP
          component="p"
          desktopFontSize={18}
          mobileFontSize={16}
          desktopFontWeight={400}
          desktopLineHeight={"150%"}
          mobileLineHeight={"145%"}
          color="inherit"
        >
          {text}
        </StyledListItemP>
      </StyledListItemTextContent>
    </StyledListItem>
  )
}

type Props = {
  data: RentProjectAdvantages
  locale: string
}
const ProjectAdvantages = ({ data, locale }: Props) => {
  const { ref, inView } = useInView({
    ...useInViewConfig,
    rootMargin: "0px",
  })

  const title = data?.title
  const subTitle = data?.subTitle
  let image = data?.image
  if (image) {
    image = getUmbracoMediaUrl(image)
  }
  const items = data?.items

  return (
    <TransitionUp ref={ref} $inView={inView} $delay={transitionDelayTime}>
      <StyledAdvantages>
        <StyledContainer locale={locale}>
          <StyledTextContent locale={locale}>
            <Box
              sx={{
                width: "100%",
                paddingInlineEnd: {
                  md: "20px",
                },
              }}
            >
              <WhiteBgHeading title={title} subTitle={subTitle} isCentered={false} />
            </Box>
            <StyledList>
              {items?.map((item, index) => (
                <AdvantageListItem key={index} item={item} locale={locale} />
              ))}
            </StyledList>
          </StyledTextContent>
          <ImageWithAlt
            className="advantages-image"
            image={{
              altText: "advantages",
              url: image,
            }}
            width={526}
            height={576}
          />
        </StyledContainer>
      </StyledAdvantages>
    </TransitionUp>
  )
}
export default ProjectAdvantages
